import { createContext, FC, useContext } from 'react';
import { useBrandHubFilterState } from 'app/modules/brandHub/hooks/useBrandHubFilterState';
import { useBrandHubGenerateCreatives } from 'app/modules/brandHub/hooks/useBrandHubGenerateCreatives';
import useAiContentQueries from 'app/modules/aiContent/hooks/useAiContentQueries';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';

type BrandHubContextType = {} & ReturnType<typeof useBrandHubFilterState> &
  ReturnType<typeof useBrandHubGenerateCreatives>;

const BrandHubContext = createContext<BrandHubContextType | undefined>(
  undefined,
);

type Props = {
  children: React.ReactNode;
};

export const BrandHubProvider: FC<Props> = ({ children }) => {
  const filterState = useBrandHubFilterState();
  const generateCreatives = useBrandHubGenerateCreatives();
  const { accountPlanType } = useSubscriptionInfo();
  const { guardClick } = useAiContentQueries({ accountPlanType });

  const guardedGenerateCreativesActions = {
    ...generateCreatives.generateCreativesActions,
    addConfig: guardClick(generateCreatives.generateCreativesActions.addConfig),
  };

  return (
    <BrandHubContext.Provider
      value={{
        ...filterState,
        ...generateCreatives,
        generateCreativesActions: guardedGenerateCreativesActions,
      }}
    >
      {children}
    </BrandHubContext.Provider>
  );
};

export const useBrandHubContext = () => {
  const context = useContext(BrandHubContext);

  if (context === undefined) {
    throw new Error(
      'useBrandHubContext must be used within a BrandHubProvider',
    );
  }

  return context;
};
